import React, {useCallback, useRef, useState} from 'react';
import graphql from 'babel-plugin-relay/macro';
import {fetchQuery} from 'relay-runtime';
import {useIntl} from 'react-intl';
import RelayEnvironment from '../../../RelayEnvironment';
import {ForecastQueryRenderer} from '../../../ForecastQueryRenderer';
import ClientContactModal, {
	ClientContactModalEntryPoint,
	ClientContactModalMode,
	ClientOption,
	ProjectOption,
} from './ClientContactModal';
import ClientContactModalProjectsLoader from './ClientContactModalProjectsLoader';
import Util from '../../shared/util/util';
import CreateClientContactMutation from '../../../mutations/CreateClientContactMutation';
import {createToast} from '../../shared/components/toasts/another-toast/toaster';
import type {ClientContactFormValues} from './hooks/useClientContactForm';

const TOAST_DURATION_MS = 5000;

export const clientContactModalContainerQuery = graphql`
	query ClientContactModalContainerQuery {
		viewer {
			component(name: "client_contact_modal")
			actualPersonId
			company {
				clients(first: 10000) @connection(key: "Company_clients", filters: []) {
					edges {
						node {
							id
							name
						}
					}
				}
			}
		}
	}
`;

// Exported so the Edit container can reuse the same compiled query (and response type) for its
// own uniqueness check, rather than defining a parallel Relay operation. See EditClientContactModalContainer.
export const emailUniqueQuery = graphql`
	query ClientContactModalContainerEmailUniqueQuery($searchQuery: ClientContactSearchQueryType) {
		viewer {
			clientContacts(first: 50, searchQuery: $searchQuery) {
				edges {
					node {
						id
						email
					}
				}
			}
		}
	}
`;

export type EmailUniqueResponse = {
	viewer?: {
		clientContacts?: {
			edges?: ReadonlyArray<{node?: {id?: string | null; email?: string | null} | null} | null> | null;
		} | null;
	} | null;
};

type ClientsEdges = ReadonlyArray<{node: {id: string; name: string} | null} | null> | null | undefined;

// The created contact, as handed to onContactSaved. Enough for a caller to show and use the
// new contact straight away, without waiting for its own query to refetch.
export type CreatedClientContact = {
	id: string;
	firstName: string | null;
	lastName: string | null;
	email: string | null;
};

type CreateClientContactResponse = {
	createClientContact?: {clientContact?: {node?: Partial<CreatedClientContact> | null} | null} | null;
};

const mapClients = (edges: ClientsEdges): ClientOption[] =>
	(edges ?? [])
		.map(e => e?.node)
		.filter((n): n is {id: string; name: string} => !!n)
		.map(n => ({id: n.id, name: n.name}));

export type ClientContactModalContainerProps = {
	mode: ClientContactModalMode;
	entryPoint: ClientContactModalEntryPoint;
	clientId?: string;
	preSelectedClientName?: string;
	// Called after a contact is successfully created, so callers can refetch their contacts list.
	// Receives the new contact, for callers that need to show or select what was just created.
	onContactSaved?: (created?: CreatedClientContact) => void;
	onClose: () => void;
};

const ClientContactModalContainer = (props: ClientContactModalContainerProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const [selectedClientId, setSelectedClientId] = useState<string>(props.clientId ?? '');
	const [isGuestUser, setIsGuestUser] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const submitLockRef = useRef(false);

	const handleGuestUserToggle = () => setIsGuestUser(prev => !prev);

	// Inline validator — only used on the module-ON path (IGNITE-538).
	// The Person-based Create / Edit flows have their own duplicate-email
	// handling driven by the mutation result.
	const validateEmailUnique = useCallback(
		async (email: string): Promise<boolean> => {
			if (!selectedClientId) return true;
			const target = email.trim().toLowerCase();
			const response = (await fetchQuery(RelayEnvironment.getInstance(), emailUniqueQuery, {
				searchQuery: {
					filters: [
						{field: 'CLIENT_CONTACT_CLIENT_ID', value: [selectedClientId]},
						{field: 'CLIENT_CONTACT_EMAIL', value: [email]},
					],
				},
			}).toPromise()) as EmailUniqueResponse | undefined;
			const edges = response?.viewer?.clientContacts?.edges ?? [];
			return !edges.some(edge => edge?.node?.email?.trim().toLowerCase() === target);
		},
		[selectedClientId]
	);

	const handleSubmit = (values: ClientContactFormValues) => {
		if (!selectedClientId || submitLockRef.current) return;
		submitLockRef.current = true;
		setIsSubmitting(true);

		const input = {
			clientId: selectedClientId,
			firstName: values.firstName,
			lastName: values.lastName,
			email: values.email,
			jobTitle: values.jobTitle || undefined,
			phone: values.phone || undefined,
			isGuestUser,
			projectIds: isGuestUser ? values.projectIds : undefined,
		};

		const onSuccess = (response?: CreateClientContactResponse) => {
			submitLockRef.current = false;
			setIsSubmitting(false);
			createToast({
				duration: TOAST_DURATION_MS,
				message: formatMessage({id: 'client_contact.snackbar.contact_created'}),
			});
			const created = response?.createClientContact?.clientContact?.node;
			props.onContactSaved?.(
				created?.id
					? {
							id: created.id,
							firstName: created.firstName ?? null,
							lastName: created.lastName ?? null,
							email: created.email ?? null,
					  }
					: undefined
			);
			props.onClose();
		};

		const onError = () => {
			submitLockRef.current = false;
			setIsSubmitting(false);
			createToast({
				duration: TOAST_DURATION_MS,
				message: formatMessage({id: 'client_contact.snackbar.contact_create_failed'}),
			});
		};

		Util.CommitMutation(CreateClientContactMutation, input, onSuccess, true, null, onError);
	};

	const renderModal = (clientOptions: ClientOption[], isClientsLoading: boolean) => {
		const presentational = (projectOptions: ProjectOption[], isProjectsLoading: boolean) => (
			<ClientContactModal
				mode={props.mode}
				entryPoint={props.entryPoint}
				clientId={selectedClientId}
				clientOptions={clientOptions}
				projectOptions={projectOptions}
				isClientsLoading={isClientsLoading}
				isProjectsLoading={isProjectsLoading}
				isGuestUser={isGuestUser}
				onGuestUserToggle={handleGuestUserToggle}
				onClientChange={setSelectedClientId}
				onClose={props.onClose}
				onSubmit={handleSubmit}
				isSubmitting={isSubmitting}
				validateEmailUnique={validateEmailUnique}
				isClientDisabled={!!props.clientId}
				preSelectedClientName={props.preSelectedClientName}
			/>
		);

		if (!selectedClientId) return presentational([], false);

		return (
			<ClientContactModalProjectsLoader clientId={selectedClientId}>
				{({projectOptions, isProjectsLoading}) => presentational(projectOptions, isProjectsLoading)}
			</ClientContactModalProjectsLoader>
		);
	};

	return (
		<ForecastQueryRenderer
			key="query-render-clientContactModal"
			query={clientContactModalContainerQuery}
			modal={true}
			alwaysRender={true}
			render={(relayProps: {viewer?: {company?: {clients?: {edges?: ClientsEdges}}}} | null) => {
				if (!relayProps || !relayProps.viewer) {
					return renderModal([], true);
				}
				const clientOptions = mapClients(relayProps.viewer.company?.clients?.edges);
				return renderModal(clientOptions, false);
			}}
		/>
	);
};

export default ClientContactModalContainer;
